import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {
  BrandSubscriptionPlatform,
  BrandSubscriptionTier,
} from '@/utils/brand-subscription/constants'
import {BrandMonitoringTierItems} from '@/utils/brand-subscription/types'
import {CaseSolutionItemType} from '@/utils/case-solution/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {PricingTabType} from '@/utils/pricing'
import {routeTo} from '@/utils/router'

import {FaqCategoryName, getFAQsContent} from '../FAQs'
import {team, TeamMemberSlug} from '../team'
import {getBrandMonitoringContent} from './brandMonitoringContent'
import {brandMonitoringTiers} from './constants'

export type BrandMonitoringCardContentType = {
  type: string
  title: string
  subtitle?: string
  badgeText: string
  priceNote?: string
  items?: BrandMonitoringTierItems
  description?: string
  button: {text: string; variant: ButtonVariant; linkHref: string; trackedEvent: string}
}

const brandMonitoringCards: BrandMonitoringCardContentType[] = [
  {
    ...brandMonitoringTiers[BrandSubscriptionTier.v2Essential],
    type: BrandSubscriptionTier.v2Essential,
    badgeText: 'brand-monitoring.monthly-report-title',
    button: {
      text: 'button-get-started',
      variant: ButtonVariant.primary,
      linkHref: routeTo.brandMonitoring.order('', BrandSubscriptionTier.v2Essential),
      trackedEvent: 'Get started is clicked (Essential)',
    },
  },
  {
    ...brandMonitoringTiers[BrandSubscriptionTier.v2Advanced],
    type: BrandSubscriptionTier.v2Advanced,
    badgeText: 'brand-monitoring.monthly-report-title',
    button: {
      text: 'button-get-started',
      variant: ButtonVariant.secondary,
      linkHref: routeTo.brandMonitoring.order('', BrandSubscriptionTier.v2Advanced),
      trackedEvent: 'Get started is clicked (Advanced)',
    },
  },
  {
    type: 'custom',
    title: 'brand-monitoring.brand-protection-card-2-title',
    subtitle: 'brand-monitoring.brand-protection-card-2-subtitle',
    badgeText: 'brand-monitoring.full-flexibility-title',
    description: 'brand-monitoring.brand-protection-card-2-desc',
    button: {
      text: 'brand-monitoring.brand-protection-card-2-cta',
      variant: ButtonVariant.secondary,
      linkHref: routeTo.bookConsultation(),
      trackedEvent: 'Request a call is clicked',
    },
  },
]

export const useBrandMonitoringContent = () => {
  const translationKey = 'brand-protection'

  return {
    ...getBrandMonitoringContent(),
    header: {
      buttonLinkText: 'start-monitoring-button',
      buttonLinkHref: routeTo.brandMonitoring.order(),
    },
    hero: {
      title: `brand-monitoring.${translationKey}-hero-title`,
      subtitle: `brand-monitoring.${translationKey}-hero-subtitle`,
      imageSrc: '/images/brand-protection/shield.svg',
      imageAlt: 'Shield with a video preview',
      list: [
        `brand-monitoring.${translationKey}-hero-list-item-0`,
        'brand-monitoring.brand-protection-hero-list-item-1',
        'brand-monitoring.brand-protection-hero-list-item-2',
      ],
      primaryButton: {
        text: 'brand-monitoring.brand-protection-hero-main-cta',
        linkHref: routeTo.brandMonitoring.order(),
      },
      secondaryButton: {
        text: 'page.menu-report-threat-title',
        linkHref: routeTo.reportBrandInfringement(),
      },
    },
    statsTitle: 'brand-protection-statistics-title',
    cards: {
      title: 'brand-monitoring.brand-protection-cards-title',
      description: 'brand-monitoring.brand-protection-cards-desc',
      badges: ['brand-monitoring.upgrade-anytime-title', 'brand-monitoring.cancel-anytime-title'],
      list: brandMonitoringCards,
    },
    expertise: {
      title: 'brand-monitoring.brand-protection-expertise-title',
      description: `brand-monitoring.brand-protection-expertise-desc`,
      imageSrc: '/images/brand-protection/brain-shield.svg',
      imageAlt: 'Half human brain and circuit board AI with a protective shield',
    },
    steps: {
      title: 'brand-monitoring.brand-protection-steps-title',
      description: 'brand-monitoring.brand-protection-steps-desc',
      button: {
        text: 'brand-monitoring.brand-protection-steps-cta',
        trackedEvent: 'Watch a demo is clicked',
      },
      list: [
        {
          title: 'brand-monitoring.brand-protection-step-0-title',
          description: `brand-monitoring.${translationKey}-step-0-desc`,
          benefits: [
            'brand-monitoring.brand-protection-step-0-benefit-0',
            'brand-monitoring.brand-protection-step-0-benefit-1',
            'brand-monitoring.brand-protection-step-0-benefit-2',
          ],
          button: {
            text: 'brand-monitoring.brand-protection-step-0-cta',
            linkHref: routeTo.brandMonitoring.order(),
            trackedEvent: 'Choose your monitoring plan is clicked (Monitoring)',
          },
          imageSrc: '/images/brand-protection/monitoring.png',
          imageAlt: 'Monitoring threats',
          imageWidth: 1520,
          imageHeight: 970,
        },
        {
          title: 'brand-monitoring.brand-protection-step-1-title',
          description: 'brand-monitoring.brand-protection-step-1-desc',
          benefits: [
            'brand-monitoring.brand-protection-step-1-benefit-0',
            'brand-monitoring.brand-protection-step-1-benefit-1',
            'brand-monitoring.brand-protection-step-1-benefit-2',
          ],
          button: {
            text: 'learn-more-text',
            linkHref: routeTo.pricing(PricingTabType.BrandProtection),
            trackedEvent: 'Learn more is clicked (Options)',
          },
          imageSrc: '/images/brand-protection/options-takedown.png',
          imageAlt: 'Monitoring solution options',
          imageWidth: 1810,
          imageHeight: 670,
        },
        {
          title: 'brand-monitoring.brand-protection-step-2-title',
          description: `brand-monitoring.${translationKey}-step-2-desc`,
          benefits: [
            'brand-monitoring.brand-protection-step-2-benefit-0',
            'brand-monitoring.brand-protection-step-2-benefit-1',
            'brand-monitoring.brand-protection-step-2-benefit-2',
          ],
          button: {
            text: 'learn-more-text',
            linkHref: routeTo.pricing(PricingTabType.BrandProtection),
            trackedEvent: 'Learn more is clicked (Enforcement)',
          },
          imageSrc: '/images/brand-protection/enforcement.png',
          imageAlt: 'Enforcement',
          imageWidth: 1610,
          imageHeight: 1190,
        },
      ],
    },
    testimonial: {
      title: 'brand-monitoring.brand-protection-testimonial-title',
      list: [
        {
          author: team[TeamMemberSlug.janBuza],
          quote: 'brand-monitoring.brand-protection-testimonial-quote-0',
        },
        {
          author: team[TeamMemberSlug.leonardoPeres],
          quote: 'brand-monitoring.brand-protection-testimonial-quote-1',
        },
        {
          author: team[TeamMemberSlug.martinaSallay],
          quote: 'brand-monitoring.brand-protection-testimonial-quote-2',
        },
        {
          author: team[TeamMemberSlug.igorDemcak],
          quote: 'brand-monitoring.brand-protection-testimonial-quote-3',
        },
        {
          author: team[TeamMemberSlug.janDohnal],
          quote: 'brand-monitoring.brand-protection-testimonial-quote-4',
        },
      ],
    },
    teamDescription: 'brand-monitoring.brand-protection-team-desc',
    ctaBanner: {
      title: 'brand-protection-cta-banner-title',
      primaryButtonLinkHref: routeTo.brandMonitoring.order(),
      primaryButtonText: 'brand-monitoring.brand-protection-cta-banner-button',
      secondaryButtonLinkHref: routeTo.pricing(PricingTabType.BrandProtection),
      secondaryButtonText: 'button-show-price-list',
      imageSrc: '/images/hero/monitoring.svg',
      imageAlt: 'Monitoring',
    },
    helpCenterCategories: getFAQsContent(FaqCategoryName.brandProtection).categories,
    promoModal: {
      description: 'brand-monitoring.promo-modal-cards-description',
      steps: {
        title: 'brand-monitoring.promo-modal-steps-title',
        description: 'brand-monitoring.promo-modal-steps-desc',
        list: [
          {
            description: 'brand-monitoring.promo-modal-step-0-desc',
            labels: [
              'brand-monitoring.promo-modal-step-0-label-0',
              'brand-monitoring.promo-modal-step-0-label-1',
            ],
          },
          {
            description: 'brand-monitoring.promo-modal-step-1-desc',
            labels: ['brand-monitoring.promo-modal-step-1-label-0'],
          },
          {
            description: 'brand-monitoring.promo-modal-step-2-desc',
            labels: [
              'brand-monitoring.promo-modal-step-2-label-0',
              'brand-monitoring.promo-modal-step-2-label-1',
            ],
          },
        ],
      },
      advanced: {
        imageSrc: '/images/icons/brand-protection-shield.svg',
        imageAlt: 'Brand protection shield',
        badgeText: 'brand-monitoring.promo-modal-badge',
        title: 'brand-monitoring.promo-modal-title',
        subtitle: 'brand-monitoring.promo-modal-subtitle',
        description: 'brand-monitoring.promo-modal-desc',
        options: [
          {
            key: MonitoringAdvancedPromoCardType.monitoring,
            title: 'brand-monitoring.promo-modal-option-0-title',
            description: 'brand-monitoring.promo-modal-option-0-desc',
            longDescription: 'brand-monitoring.promo-modal-option-0-long-desc',
            cover: {
              src: '/images/brand-protection/promo-monitoring.png',
              alt: 'Monitoring',
            },
            badges: [
              'brand-monitoring.upgrade-anytime-title',
              'brand-monitoring.cancel-anytime-title',
              'brand-monitoring.monthly-report-title',
            ],
            platforms: {
              title: 'brand-monitoring.promo-modal-option-0-section-0-title',
              items: [
                {
                  keys: [BrandSubscriptionPlatform.trademarkRegisters],
                  title: 'brand-monitoring.promo-modal-option-0-section-0-item-0-title',
                  description: 'brand-monitoring.promo-modal-option-0-section-0-item-0-desc',
                  tooltipText: 'brand-monitoring.promo-modal-option-0-section-0-item-0-tooltip',
                },
                {
                  keys: [BrandSubscriptionPlatform.businessRegisters],
                  title: 'brand-monitoring.promo-modal-option-0-section-0-item-1-title',
                  description: 'brand-monitoring.promo-modal-option-0-section-0-item-1-desc',
                  tooltipText: 'brand-monitoring.promo-modal-option-0-section-0-item-1-tooltip',
                },
                {
                  keys: [BrandSubscriptionPlatform.webDomains],
                  title: 'brand-monitoring.promo-modal-option-0-section-0-item-2-title',
                  description: 'brand-monitoring.promo-modal-option-0-section-0-item-2-desc',
                },
                {
                  keys: [
                    BrandSubscriptionPlatform.facebook,
                    BrandSubscriptionPlatform.instagram,
                    BrandSubscriptionPlatform.youtube,
                    BrandSubscriptionPlatform.tiktok,
                  ],
                  title: 'brand-monitoring.promo-modal-option-0-section-0-item-3-title',
                  description: 'brand-monitoring.promo-modal-option-0-section-0-item-3-desc',
                },
                {
                  keys: [
                    BrandSubscriptionPlatform.amazon,
                    BrandSubscriptionPlatform.aliExpress,
                    BrandSubscriptionPlatform.etsy,
                    BrandSubscriptionPlatform.ebay,
                  ],
                  title: 'brand-monitoring.promo-modal-option-0-section-0-item-4-title',
                  description: 'brand-monitoring.promo-modal-option-0-section-0-item-4-desc',
                },
                {
                  keys: [BrandSubscriptionPlatform.appStore, BrandSubscriptionPlatform.googlePlay],
                  title: 'brand-monitoring.promo-modal-option-0-section-0-item-5-title',
                  description: 'brand-monitoring.promo-modal-option-0-section-0-item-5-desc',
                },
              ],
            },
          },
          {
            key: MonitoringAdvancedPromoCardType.legalEnforcement,
            title: 'brand-monitoring.promo-modal-option-1-title',
            description: 'brand-monitoring.promo-modal-option-1-desc',
            longDescription: 'brand-monitoring.promo-modal-option-1-long-desc',
            cover: {
              src: '/images/brand-protection/promo-legal-enforcement.png',
              alt: 'Legal enforcement',
            },
            badges: [
              'brand-monitoring.free-consultation-title',
              'brand-monitoring.custom-solutions-title',
            ],
            services: [
              {
                title: 'brand-monitoring.promo-modal-option-1-section-0-title',
                items: [
                  {
                    service: {
                      type: CaseSolutionItemType.takedown,
                      platform: BrandSubscriptionPlatform.amazon,
                    },
                    title: 'brand-monitoring.promo-modal-option-1-section-0-item-0-title',
                    description: 'brand-monitoring.promo-modal-option-1-section-0-item-0-desc',
                  },
                  {
                    service: {
                      type: CaseSolutionItemType.ceaseDesistLetter,
                      platform: undefined,
                      option: 'simple',
                    },
                    title: 'brand-monitoring.promo-modal-option-1-section-0-item-1-title',
                    description: 'brand-monitoring.promo-modal-option-1-section-0-item-1-desc',
                  },
                  {
                    service: {type: CaseSolutionItemType.warningLetter},
                    title: 'brand-monitoring.promo-modal-option-1-section-0-item-2-title',
                    description: 'brand-monitoring.promo-modal-option-1-section-0-item-2-desc',
                  },
                ],
              },
              {
                title: 'brand-monitoring.promo-modal-option-1-section-1-title',
                feeNote: 'brand-monitoring.promo-modal-option-1-section-1-subtitle',
                items: [
                  {
                    title: 'brand-monitoring.promo-modal-option-1-section-1-item-0-title',
                    description: 'brand-monitoring.promo-modal-option-1-section-1-item-0-desc',
                  },
                  {
                    title: 'brand-monitoring.promo-modal-option-1-section-1-item-1-title',
                    description: 'brand-monitoring.promo-modal-option-1-section-1-item-1-desc',
                  },
                  {
                    title: 'brand-monitoring.promo-modal-option-1-section-1-item-2-title',
                    description: 'brand-monitoring.promo-modal-option-1-section-1-item-2-desc',
                  },
                ],
              },
            ],
          },
        ] as MonitoringAdvancedPromoCardContentType[],
      },
    },
    overview: {
      title: 'brand-protection-overview-title',
      description: 'brand-protection-overview-desc',
      subtitle: 'brand-protection-overview-subtitle',
      moreIcon: {imageSrc: '/images/more-horizontal-with-gradient.svg', imageAlt: 'More icon'},
      button: {text: 'brand-protection-overview-cta', linkHref: routeTo.brandMonitoring.home()},
    },
  }
}

export enum MonitoringAdvancedPromoCardType {
  monitoring = 'monitoring',
  legalEnforcement = 'legal-enforcement',
}

export type MonitoringAdvancedPromoCardContentType = {
  key: MonitoringAdvancedPromoCardType
  title: string
  description: string
  longDescription: string
  cover: {src: string; alt: string}
  badges: string[]
  platforms?: {
    title: string
    items: {
      keys: BrandSubscriptionPlatform[]
      title: string
      description: string
      tooltipText?: string
    }[]
  }
  services?: {
    title: string
    feeNote?: string
    items: {
      service?: {
        type: CaseSolutionItemType
        platform?: BrandSubscriptionPlatform
        jurisdiction?: Jurisdiction
        option?: string
      }
      title: string
      description: string
    }[]
  }[]
}
